import React, { useEffect, useCallback } from 'react';
import { StyleSheet, BackHandler, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { TaskQueue } from '../components/TaskQueue';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { StrokeSyncService } from '../services/StrokeSyncService';

type Props = StackScreenProps<RootStackParamList, 'TaskQueue'>;

export function TaskQueueScreen({ navigation }: Props) {
    const { studentName, studentId, groupId, leaveClass, switchStudent } = useAuthStore();
    const { activities, currentActivityIndex, resetActivities } = useActivityStore();

    const isStudentCheckedIn = Boolean(studentName || studentId || groupId);

    const handleSwitchStudent = useCallback(() => {
        StrokeSyncService.stopSync();
        resetActivities();
        switchStudent();
        // Navigate back to roster instead of full reset - preserves class context
        const { classId, serverUrl } = useAuthStore.getState();
        if (classId && serverUrl) {
            navigation.replace('RosterPick', { classId, serverUrl });
        } else {
            leaveClass();
            navigation.replace('Home');
        }
    }, [navigation, resetActivities, switchStudent, leaveClass]);

    // If no student is checked in, immediately return to the roster screen (or Home)
    useEffect(() => {
        if (!isStudentCheckedIn) {
            const { classId, serverUrl } = useAuthStore.getState();
            if (classId && serverUrl) {
                navigation.replace('RosterPick', { classId, serverUrl });
            } else {
                leaveClass();
                navigation.replace('Home');
            }
        }
    }, [isStudentCheckedIn, navigation, leaveClass]);

    // Handle hardware back press: switch student / return to roster
    useEffect(() => {
        if (Platform.OS === 'web') return;
        const onBackPress = () => {
            handleSwitchStudent();
            return true;
        };
        const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);
        return () => subscription.remove();
    }, [handleSwitchStudent]);

    if (!isStudentCheckedIn) {
        return null;
    }

    const handleStartTasks = () => {
        navigation.navigate('ActiveLesson');
    };

    return (
        <SafeAreaView style={styles.container} accessibilityLabel="Task queue screen" accessibilityRole="none">
            <TaskQueue
                studentName={studentName || ''}
                tasks={activities}
                currentActivityIndex={currentActivityIndex}
                loading={false}
                onStartTasks={handleStartTasks}
                onSwitchStudent={handleSwitchStudent}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#E6F7FF' }
});
